import { create } from "zustand";
import { AppRecordJK, AppRecordStatusJK, RequestApi, RouteApi, StatusMsg } from "utils";
import { AppLog } from "services";
import { Service } from "utils/services";
import { requestSubmitFollowingUser } from "api-function/requestFriendStatusApi";
import { onPostReaction } from "api-function/postContentApi";
import { PostReaction } from "models/postReaction";

interface NewFeedState {
  posts: any[];

  appRecord: AppRecordJK;
  postIds: Set<string>;
  initializePage: () => Promise<void>;
  fetchPosts: (isRefresh: boolean) => Promise<void>;
  onClickedLike: (post: any) => Promise<void>;
  onClickedShare: (post: any) => Promise<void>;
  onClickedFollow: (post: any, userId: any) => Promise<void>;

  onSuggestionFollowingUser: (post: any, userId: string) => Promise<void>;
  onSuggestionUnFollowingUser: (post: any, userId: string) => Promise<void>;
  onSuggestionUninterestUser: (post: any, userId: string) => Promise<void>;

  connectorUsers: any[];
  connectorUsersLoading: boolean;
  birthdayUsers: any[];
  birthdayUsersLoading: boolean;
  lastActiveShopId: string;

  fetchFriendUserBirthday: () => Promise<void>;
  fetchFriendUser: () => Promise<void>;

  onPostUpdate: (postUpdate: any) => Promise<void>;
}

export const newFeedStore = create<NewFeedState>((set, get) => ({
  posts: [],
  appRecord: new AppRecordJK({ loadingStatus: AppRecordStatusJK.loaded }),
  postIds: new Set<string>(),

  initializePage: async () => {
    try {
      set({
        posts: [],
        appRecord: new AppRecordJK({ loadingStatus: AppRecordStatusJK.loaded }),
        postIds: new Set<string>(),

        birthdayUsers: [],
        birthdayUsersLoading: true,
        lastActiveShopId: "",

        connectorUsers: [],
        connectorUsersLoading: true,
      });

      await newFeedStore.getState().fetchPosts(true);
      await newFeedStore.getState().fetchFriendUserBirthday();
      await newFeedStore.getState().fetchFriendUser();
    } catch (error) {
      AppLog.show({
        message: `[initializePage]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  fetchPosts: async (isRefresh: boolean) => {
    const { posts, postIds, appRecord } = get();
    let tmpAppRecord = new AppRecordJK({ ...appRecord });
    try {
      if (tmpAppRecord.canLoadMore() === false) return;

      if (!isRefresh) {
        tmpAppRecord.loadingStatus = AppRecordStatusJK.loadingMore;
        set({ appRecord: tmpAppRecord });
      }

      let url = RouteApi.getNewFeedWeb;
      url += `token=${Service.token}`;

      const postIdsArray: string[] = [...postIds];
      const dataPosts = { post_ids: postIdsArray, tag: [] };
      const responses = await RequestApi(url, { data: dataPosts });
      if (responses.status === "success") {
        const newPosts: any[] = responses.posts;
        const filteredPosts = newPosts.filter((post) => !postIds.has(post.provider_post_id));

        const newPostIds = new Set(postIds);
        filteredPosts.forEach((post) => newPostIds.add(post.provider_post_id));
        tmpAppRecord.loadingStatus = AppRecordStatusJK.loaded;

        set({
          posts: [...posts, ...filteredPosts],
          postIds: newPostIds,
          appRecord: tmpAppRecord,
        });
      } else {
        tmpAppRecord.loadingStatus = AppRecordStatusJK.loaded;
        set({
          appRecord: tmpAppRecord,
        });
      }
    } catch (error) {
      tmpAppRecord.loadingStatus = AppRecordStatusJK.loaded;
      set({
        appRecord: tmpAppRecord,
      });

      AppLog.show({
        message: `[fetchPosts]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  onClickedLike: async (post: any) => {
    try {
      const { posts } = get();
      if (Service.userAuth) {
        const attachment: any = post?.post_attachments[0];
        const isLike: boolean = !Boolean(Service.toInteger(post?.is_reaction_like));

        const responses = await onPostReaction({
          content: post,
          reaction: PostReaction.like,
          isLike: isLike,
          isFavorite: false,
          attachment,
        });
        if (responses.status === "success") {
          let tmpPost = structuredClone(post);
          tmpPost.is_reaction_like = isLike ? "1" : "0";
          const numberLike = isLike ? 1 : -1;
          tmpPost.reaction_like_count = Service.toInteger(tmpPost.reaction_like_count) + numberLike;

          if (isLike) {
            const userAuth = { ...Service.userAuth };
            tmpPost.like_users = [...tmpPost.like_users, userAuth];
          } else {
            tmpPost.like_users = tmpPost.like_users.filter((user: any) => user.id.toString() !== Service.userAuth.id.toString());
          }

          const editIndex = posts.findIndex((post) => post.provider_post_id === tmpPost.provider_post_id);
          if (editIndex !== -1) {
            set((state) => {
              const updatedPosts = [...state.posts];
              updatedPosts[editIndex] = tmpPost;
              return { posts: updatedPosts };
            });
          }
        } else {
        }
      }
    } catch (error) {
      AppLog.show({
        message: `[onClickedLike]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  onClickedShare: async (post: any) => {
    try {
      const { posts } = get();
      const tmpPost = { ...post };

      const editIndex = posts.findIndex((post) => post.provider_post_id === tmpPost.provider_post_id);
      if (editIndex !== -1) {
        set((state) => {
          const updatedPosts = [...state.posts];
          updatedPosts[editIndex] = tmpPost;
          return { posts: updatedPosts };
        });
      }
    } catch (error) {
      AppLog.show({
        message: `[onClickedShare]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  onClickedFollow: async (post: any, userId: string) => {
    try {
      const { posts } = get();

      const responses = await requestSubmitFollowingUser({ userId: userId });
      if (responses.status === "success") {
        let tmpPost = structuredClone(post);

        if (tmpPost.re_post_user.id.toString() === userId) {
          tmpPost.re_post_user.friend_status = responses.user?.friend_status ?? null;
        }
        const editIndex = posts.findIndex((post) => post.provider_post_id === tmpPost.provider_post_id);
        if (editIndex !== -1) {
          set((state) => {
            const updatedPosts = [...state.posts];
            updatedPosts[editIndex] = tmpPost;
            return { posts: updatedPosts };
          });
        }
      } else {
      }
    } catch (error) {
      AppLog.show({
        message: `[onClickedFollow]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  onSuggestionUninterestUser: async (post: any, userId: string) => {
    try {
      const { posts } = get();
      let url = RouteApi.submitUninterestUser;
      url += `token=${Service.token}`;
      url += `&user_id=${userId}`;
      const responses = await RequestApi(url);
      if (responses.status === "success") {
        let tmpPost = structuredClone(post);
        const indexUser = tmpPost.suggestion_users.findIndex((user: any) => `${user.id}` === userId);

        if (indexUser > -1) {
          tmpPost.suggestion_users.splice(indexUser, 1);

          const editIndex = posts.findIndex((post) => post.provider_post_id === tmpPost.provider_post_id);
          if (editIndex !== -1) {
            set((state) => {
              const updatedPosts = [...state.posts];
              updatedPosts[editIndex] = tmpPost;
              return { posts: updatedPosts };
            });
          }
        }
      } else {
      }
    } catch (error) {
      AppLog.show({
        message: `[onSuggestionUninterestUser]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  onSuggestionFollowingUser: async (post: any, userId: string) => {
    try {
      const { posts } = get();
      let url = RouteApi.submitFollowingUser;
      url += `token=${Service.token}`;
      url += `&user_id=${userId}`;
      const responses = await RequestApi(url);
      if (responses.status === "success") {
        let tmpPost = structuredClone(post);
        const indexUser = tmpPost.suggestion_users.findIndex((user: any) => `${user.id}` === userId);
        if (indexUser > -1) {
          tmpPost.suggestion_users[indexUser].friend_status = "Following";

          const editIndex = posts.findIndex((post) => post.provider_post_id === tmpPost.provider_post_id);
          if (editIndex !== -1) {
            set((state) => {
              const updatedPosts = [...state.posts];
              updatedPosts[editIndex] = tmpPost;
              return { posts: updatedPosts };
            });
          }
        }
      } else {
      }
    } catch (error) {
      AppLog.show({
        message: `[onSuggestionFollowingUser]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  onSuggestionUnFollowingUser: async (post: any, userId: string) => {
    try {
      const { posts } = get();
      let url = RouteApi.submitUnFollowingUser;
      url += `token=${Service.token}`;
      url += `&user_id=${userId}`;
      const responses = await RequestApi(url);
      if (responses.status === "success") {
        let tmpPost = structuredClone(post);

        const indexUser = tmpPost.suggestion_users.findIndex((user: any) => `${user.id}` === userId);

        if (indexUser > -1) {
          tmpPost.suggestion_users[indexUser].friend_status = "None";

          const editIndex = posts.findIndex((post) => post.provider_post_id === tmpPost.provider_post_id);
          if (editIndex !== -1) {
            set((state) => {
              const updatedPosts = [...state.posts];
              updatedPosts[editIndex] = tmpPost;
              return { posts: updatedPosts };
            });
          }
        }
      } else {
      }
    } catch (error) {
      AppLog.show({
        message: `[onSuggestionUnFollowingUser]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  // ------
  birthdayUsers: [],
  birthdayUsersLoading: true,
  lastActiveShopId: "",

  connectorUsers: [],
  connectorUsersLoading: true,

  fetchFriendUserBirthday: async () => {
    try {
      let url = RouteApi.getFriendUserBirthday;
      url += `token=${Service?.token}`;
      const responses = await RequestApi(url);
      if (responses.status === "success") {
        set({
          birthdayUsers: responses.friend_users.data,
          lastActiveShopId: responses.last_active_shop_id,
        });
      } else {
      }
    } catch (error) {
      AppLog.show({
        message: `[fetchFriendUserBirthday]`,
        status: StatusMsg.error,
        error,
      });
    } finally {
      set({ connectorUsersLoading: false });
    }
  },

  fetchFriendUser: async () => {
    try {
      let url = RouteApi.getFriendUser;
      url += `token=${Service?.token}`;
      const responses = await RequestApi(url);
      if (responses.status === "success") {
        set({
          connectorUsers: responses.friend_users.data,
        });
      } else {
      }
    } catch (error) {
      AppLog.show({
        message: `[fetchFriendUser]`,
        status: StatusMsg.error,
        error,
      });
    } finally {
      set({ birthdayUsersLoading: false });
    }
  },

  onPostUpdate: async (postUpdate: any) => {
    try {
      const { posts } = get();
      const editIndex = posts.findIndex((post) => post.provider_post_id === postUpdate.provider_post_id);
      if (editIndex !== -1) {
        set((state) => {
          const updatedPosts = [...state.posts];
          updatedPosts[editIndex] = postUpdate;
          return { posts: updatedPosts };
        });
      }
    } catch (error) {
      AppLog.show({
        message: `[onPostUpdate]`,
        status: StatusMsg.error,
        error,
      });
    }
  },
}));

